import React from 'react';
import PropTypes from 'prop-types';
import { Calendar, Clock, Check, ArrowRight, Mountain, Instagram, Facebook, ExternalLink, Music } from 'lucide-react';

const toSoundCloudEmbed = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('soundcloud.com')) {
      return `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&visual=false`;
    }
    if (u.hostname.includes('w.soundcloud.com')) return url;
  } catch {}
  return null;
};

const toSpotifyEmbed = (url) => {
  if (!url) return null;
  try {
    const u = new URL(url);
    if (u.hostname.includes('open.spotify.com')) {
      const parts = u.pathname.split('/').filter(Boolean);
      if (parts.length >= 2) {
        const type = parts[0];
        const id = parts[1];
        return `https://open.spotify.com/embed/${type}/${id}`;
      }
    }
    if (u.hostname.includes('spotify.com') && u.pathname.includes('/embed/')) return url;
  } catch {}
  return null;
};

const LineupCard = ({ lineup }) => {
  if (!lineup || !lineup.visible || !Array.isArray(lineup.artists) || lineup.artists.length === 0) return null;
  return (
    <div className="">
      <h5 className="mb-3 text-sm uppercase tracking-[0.3em] text-white">Line-up</h5>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {lineup.artists.map((a, idx) => (
          <div key={idx} className="overflow-hidden relative rounded-lg border transition-colors bg-gray-900/50 border-white/10 hover:border-white/20">
            {a.role && (
              <span className="absolute top-2 right-2 px-3 py-1 text-xs font-semibold tracking-wider text-white uppercase rounded-full border shadow-sm backdrop-blur-sm bg-black/70 border-white/20">
                {a.role}
              </span>
            )}
            {a.avatarUrl && (
              <div className="w-full aspect-video bg-white/5">
                <img src={a.avatarUrl} alt={a.name} className="object-cover w-full h-full" />
              </div>
            )}
            <div className="p-3 text-center">
              <span className="font-semibold text-white">{a.name}</span>
            </div>
            {(a.startTime || a.endTime) && (
              <div className="flex justify-center mt-1">
                <span className="inline-flex gap-2 items-center px-3 py-1 text-xs text-gray-300 rounded-full bg-white/5">
                  <Clock size={12} className="text-gray-400" />
                  {a.startTime || ''}{a.endTime ? ` – ${a.endTime}` : ''}
                </span>
              </div>
            )}
            {(a.soundcloudUrl || a.spotifyUrl) && (
              <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                {toSoundCloudEmbed(a.soundcloudUrl) ? (
                  <iframe title={`sc-${idx}`} width="100%" height="100" scrolling="no" frameBorder="no" allow="autoplay" src={toSoundCloudEmbed(a.soundcloudUrl)} className="rounded" />
                ) : null}
                {toSpotifyEmbed(a.spotifyUrl) ? (
                  <iframe title={`sp-${idx}`} style={{ borderRadius: 12 }} src={toSpotifyEmbed(a.spotifyUrl)} width="100%" height="100" frameBorder="0" allow="encrypted-media" className="rounded" />
                ) : null}
              </div>
            )}
            {((a.soundcloudUrl && !toSoundCloudEmbed(a.soundcloudUrl)) || (a.spotifyUrl && !toSpotifyEmbed(a.spotifyUrl))) && (
              <div className="flex gap-2 mt-3">
                {a.soundcloudUrl && !toSoundCloudEmbed(a.soundcloudUrl) && (
                  <a href={a.soundcloudUrl} target="_blank" rel="noopener noreferrer" aria-label="SoundCloud" className="inline-flex flex-1 justify-center items-center h-10 text-gray-300 rounded bg-white/10 hover:text-white hover:bg-white/20">
                    <Music size={18} />
                  </a>
                )}
                {a.spotifyUrl && !toSpotifyEmbed(a.spotifyUrl) && (
                  <a href={a.spotifyUrl} target="_blank" rel="noopener noreferrer" aria-label="Spotify" className="inline-flex flex-1 justify-center items-center h-10 text-gray-300 rounded bg-white/10 hover:text-white hover:bg-white/20">
                    <ExternalLink size={18} />
                  </a>
                )}
              </div>
            )}
            {(a.instagram || a.facebook || a.otherLink) && (
              <div className="flex gap-2 mt-3">
                {a.instagram && (
                  <a href={a.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="inline-flex flex-1 justify-center items-center h-10 text-gray-300 rounded bg-white/10 hover:text-white hover:bg-white/20">
                    <Instagram size={18} />
                  </a>
                )}
                {a.facebook && (
                  <a href={a.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="inline-flex flex-1 justify-center items-center h-10 text-gray-300 rounded bg-white/10 hover:text-white hover:bg-white/20">
                    <Facebook size={18} />
                  </a>
                )}
                {a.otherLink && (
                  <a href={a.otherLink} target="_blank" rel="noopener noreferrer" aria-label="Lien" className="inline-flex flex-1 justify-center items-center h-10 text-gray-300 rounded bg-white/10 hover:text-white hover:bg-white/20">
                    <ExternalLink size={18} />
                  </a>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

LineupCard.propTypes = {
  lineup: PropTypes.shape({ visible: PropTypes.bool, artists: PropTypes.arrayOf(PropTypes.object) })
};

const Events = ({ pastEvents, upcomingEvents, loading, lineupsByEventId, onReserveClick }) => {
  if (loading) {
    return (
      <section id="events" className="px-6 py-24 bg-black md:py-32">
        <div className="mx-auto max-w-5xl text-center">
          <p className="text-gray-500">Chargement des événements...</p>
        </div>
      </section>
    );
  }

  return (
    <section id="events" className="relative px-6 py-24 bg-gradient-to-b to-black from-gray-950/95 md:py-32">

      <div className="relative z-10 mx-auto max-w-5xl">
        <div className="mb-16 text-center">
          <h2 className="mb-4 text-4xl font-black tracking-tight md:text-6xl">
            LES ÉDITIONS
          </h2>
          <p className="text-lg text-gray-400">Chaque volume, une nouvelle histoire</p>
        </div>

        <div className="grid gap-8 md:grid-cols-2">
          {/* Past Events */}
          <div className="order-2 md:order-1">
            <h3 className="text-sm uppercase tracking-[0.3em] text-gray-500 mb-6 flex items-center gap-2">
              <Check size={16} className="text-green-500" />
              Éditions passées
            </h3>
            
            <div className="space-y-4">
              {pastEvents.length === 0 ? (
                <div className="p-6 text-center text-gray-500 rounded-lg bg-gray-900/40">
                  Aucune édition passée
                </div>
              ) : pastEvents.map((event) => (
                <div 
                  key={event.id}
                  className="p-6 rounded-lg transition-colors group bg-gray-900/40 hover:bg-gray-900/60"
                >
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <span className="text-xs tracking-widest text-gray-500 uppercase">Vol.{event.volume}</span>
                      <h4 className="flex gap-2 items-center mt-1 text-xl font-bold">
                        <Mountain size={18} className="text-gray-400" />
                        {event.theme}
                      </h4>
                    </div>
                    <span className="px-3 py-1 text-xs tracking-wider text-gray-300 uppercase rounded-full bg-white/10">
                      Terminé
                    </span>
                  </div>
                  
                  <div className="flex gap-4 items-center mb-4 text-sm text-gray-400">
                    <div className="flex gap-1 items-center">
                      <Calendar size={14} />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex gap-1 items-center">
                      <Clock size={14} />
                      <span>{event.time}</span>
                    </div>
                  </div>

                  {event.description && (
                    <p className="mb-4 text-sm text-gray-500" style={{ whiteSpace: 'pre-line' }}>{event.description}</p>
                  )}
                  <LineupCard lineup={lineupsByEventId?.[event.id]} />
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Events */}
          <div className="order-1 md:order-2">
            <h3 className="text-sm uppercase tracking-[0.3em] text-gray-500 mb-6 flex items-center gap-2">
              <ArrowRight size={16} className="text-white" />
              Prochaine édition
            </h3>
            
            <div className="space-y-4">
              {upcomingEvents.length === 0 ? (
                <div className="p-6 text-center text-gray-500 rounded-lg bg-gray-900/50 border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.10)_inset,0_0_28px_rgba(255,255,255,0.06)]">
                  Aucun événement à venir pour le moment
                </div>
              ) : upcomingEvents.map((event) => (
                <div 
                  key={event.id}
                  className="overflow-hidden relative p-6 bg-gradient-to-br from-gray-900 rounded-lg to-gray-950 group border border-white/10 shadow-[0_0_0_1px_rgba(255,255,255,0.10)_inset,0_0_28px_rgba(255,255,255,0.06)] transition-transform duration-300 hover:scale-[1.01]"
                >
                  <div className="pointer-events-none absolute -inset-px rounded-[inherit] bg-gradient-to-br from-white/10 via-transparent to-transparent opacity-25" />
                  <div className="absolute inset-0 mix-blend-screen pointer-events-none">
                    <div
                      className="absolute -inset-12 rounded-[inherit] opacity-20 animate-[spin_18s_linear_infinite]"
                      style={{
                        backgroundImage: 'conic-gradient(from 0deg, rgba(255,255,255,0.12) 0deg, transparent 40deg, transparent 320deg, rgba(255,255,255,0.12) 360deg)',
                        WebkitMaskImage: 'radial-gradient(circle at 50% 50%, transparent 0 52%, black 66%, transparent 100%)',
                        maskImage: 'radial-gradient(circle at 50% 50%, transparent 0 52%, black 66%, transparent 100%)'
                      }}
                    />
                  </div>
                  <div
                    className="absolute top-0 left-0 right-0 h-[2px] opacity-40"
                    style={{ backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)' }}
                  />
                  <div className="absolute top-0 right-0 w-28 h-28 rounded-full blur-3xl bg-white/5" />
                  
                  <div className="relative">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <span className="text-xs tracking-widest text-gray-500 uppercase">Vol.{event.volume}</span>
                        <h4 className="flex gap-2 items-center mt-1 text-2xl font-bold">
                          {event.theme}
                        </h4>
                      </div>
                      <span className="px-3 py-1 text-xs tracking-wider text-green-400 uppercase rounded-full bg-green-500/20 shadow-[0_0_0_1px_rgba(0,0,0,0.25)_inset] animate-pulse">
                        {event.status === 'available' ? 'Ouvert' : event.status === 'sold_out' ? 'Complet' : 'Bientôt'}
                      </span>
                    </div>
                    
                    <div className="flex gap-4 items-center mb-6 text-sm text-gray-300">
                      <div className="flex gap-1 items-center">
                        <Calendar size={14} />
                        <span>{event.date}</span>
                      </div>
                      <div className="flex gap-1 items-center">
                        <Clock size={14} />
                        <span>{event.time}</span>
                      </div>
                    </div>

                    {event.description && (
                      <p className="mb-6 text-gray-400" style={{ whiteSpace: 'pre-line' }}>{event.description}</p>
                    )}
                    <LineupCard lineup={lineupsByEventId?.[event.id]} />

                    <div className="flex justify-between items-center pt-4">
                      {event.reservationsOpen ? (
                        <button
                          onClick={() => onReserveClick && onReserveClick()}
                          className="px-6 py-2 text-sm font-semibold tracking-wider text-black uppercase bg-white rounded-full transition-colors hover:bg-gray-200"
                        >
                          Réserver
                        </button>
                      ) : (
                        <span className="text-sm text-gray-500">Réservations bientôt ouvertes</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Instagram CTA */}
            <div className="p-4 mt-6 text-center rounded-lg border bg-gray-900/50 border-white/10">
              <p className="mb-2 text-sm text-gray-400">Soyez les premiers informés</p>
              <a 
                href="https://instagram.com/crapule_bxl" 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-semibold text-white hover:underline"
              >
                @crapule_bxl sur Instagram
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Events;
